/**
 * adminModuleMap — çoklu admin (subadmin) sistemi merkezi modül haritası.
 *
 * Tek kaynak: Sidebar grup/buton yapısıyla birebir aynı anahtarlar.
 * Kullanım noktaları:
 *  - AdminUserPermissionsModal: modül ağacı (grup toggle + tekil buton checkbox'ları)
 *  - Sidebar: subadmin için grup/buton filtreleme (deny-by-default)
 *  - App.tsx SubAdminModuleGuard: rota → modül izni kontrolü
 *  - GlobalSearch: arama sonuçlarının izin haritasıyla filtrelenmesi
 *
 * İzin kaydı SettingsJson formatı (SystemUserScreenPermissions, ScreenType='adminPanel'):
 *   { [sectionId]: { enabled: boolean, items: { [itemId]: boolean } } }
 * Deny-by-default: kayıtta olmayan her şey İZİNSİZDİR.
 */

export interface AdminSectionPermission {
  enabled: boolean;
  items: Record<string, boolean>;
}

export type AdminPermissionsMap = Record<string, AdminSectionPermission>;

export interface AdminModuleItem {
  /** Sidebar buton id'si (isItemVisible ile aynı anahtar). */
  id: string;
  labelTr: string;
  labelEn: string;
  /** Bu modüle ait rotalar; ilk eleman birincil navigasyon hedefidir. Prefix eşleşir. */
  paths: string[];
}

export interface AdminModuleGroup {
  /** Sidebar section id'si (visibleSections ile aynı anahtar). */
  id: string;
  labelTr: string;
  labelEn: string;
  items: AdminModuleItem[];
}

/** Sidebar başlık + buton yapısıyla birebir aynı sıra. */
export const ADMIN_MODULE_GROUPS: AdminModuleGroup[] = [
  {
    // Sidebar'ın en üstündeki bağımsız yeşil "Yönetici Ekranı" butonu — ayrı izne tabidir.
    // (Ön Muhasebe grubundaki "Yönetici Ekranı" satırı kendi izniyle ayrıca yönetilir;
    //  rota her iki izinden biri açıksa erişilebilir.)
    id: 'admin_screen',
    labelTr: 'Yönetici Ekranı',
    labelEn: 'Admin Screen',
    items: [
      { id: 'adminScreen', labelTr: 'Yönetici Ekranı (üst buton)', labelEn: 'Admin Screen (top button)', paths: ['/accounting/dashboard'] },
    ],
  },
  {
    id: 'task_tracking',
    labelTr: 'Görev Takip',
    labelEn: 'Task Tracking',
    items: [
      { id: 'dashboard', labelTr: 'Dashboard', labelEn: 'Dashboard', paths: ['/dashboard'] },
      { id: 'tasks', labelTr: 'Görevler', labelEn: 'Tasks', paths: ['/tasks'] },
      { id: 'reports', labelTr: 'Raporlar', labelEn: 'Reports', paths: ['/reports'] },
      { id: 'messages', labelTr: 'Mesajlar', labelEn: 'Messages', paths: ['/messages'] },
      { id: 'surveys', labelTr: 'Anketler', labelEn: 'Surveys', paths: ['/surveys'] },
    ],
  },
  {
    id: 'hr',
    labelTr: 'İnsan Kaynakları',
    labelEn: 'Human Resources',
    items: [
      { id: 'personnel', labelTr: 'Personel', labelEn: 'Personnel', paths: ['/personnel'] },
      { id: 'shifts', labelTr: 'Vardiyalar', labelEn: 'Shifts', paths: ['/shifts'] },
      { id: 'pdks', labelTr: 'PDKS', labelEn: 'PDKS', paths: ['/hr/pdks'] },
      { id: 'recruitment', labelTr: 'İşe Alım', labelEn: 'Recruitment', paths: ['/ik/isealim'] },
      { id: 'cvApplications', labelTr: 'CV Başvuruları', labelEn: 'CV Applications', paths: ['/hr/cv-application'] },
    ],
  },
  {
    id: 'accounting',
    labelTr: 'Ön Muhasebe',
    labelEn: 'Pre-Accounting',
    items: [
      { id: 'dashboard', labelTr: 'Yönetici Ekranı', labelEn: 'Admin Screen', paths: ['/accounting/dashboard'] },
      { id: 'current', labelTr: 'Cari Hesaplar', labelEn: 'Current Accounts', paths: ['/cari', '/cari-hareket-raporu'] },
      { id: 'stock', labelTr: 'Ürün ve Stok', labelEn: 'Products and Stock', paths: ['/stok', '/stok/lotlar', '/stok-hareket-raporu', '/fiyat-degisim-loglari', '/basit-uretim-receteleri', '/urun-menuleri'] },
      { id: 'bank', labelTr: 'Banka', labelEn: 'Bank', paths: ['/banka', '/bank/transactions'] },
      { id: 'cash', labelTr: 'Kasa', labelEn: 'Cashbox', paths: ['/kasa', '/accounting/cashbox'] },
      { id: 'checksNotes', labelTr: 'Çek/Senet', labelEn: 'Checks/Notes', paths: ['/cek-senet'] },
      { id: 'loans', labelTr: 'Krediler', labelEn: 'Loans', paths: ['/krediler', '/kredi-odemeleri'] },
      { id: 'projects', labelTr: 'Projeler', labelEn: 'Projects', paths: ['/projeler'] },
      { id: 'profitLoss', labelTr: 'Kâr-Zarar', labelEn: 'Profit & Loss', paths: ['/kar-zarar'] },
      { id: 'expenseCategories', labelTr: 'Gider Türleri', labelEn: 'Expense Types', paths: ['/gider-turleri'] },
      { id: 'invoice', labelTr: 'Faturalar', labelEn: 'Invoices', paths: ['/fatura/alis', '/fatura/satis', '/fatura/raporlar'] },
      { id: 'eInvoice', labelTr: 'E-Fatura', labelEn: 'E-Invoice', paths: ['/e-fatura'] },
      { id: 'offers', labelTr: 'Teklifler', labelEn: 'Offers', paths: ['/teklifler'] },
      { id: 'waybills', labelTr: 'İrsaliyeler', labelEn: 'Waybills', paths: ['/irsaliyeler', '/irsaliye'] },
      { id: 'orders', labelTr: 'Siparişler', labelEn: 'Orders', paths: ['/siparisler', '/siparis'] },
      { id: 'interCompanyB2B', labelTr: 'B2B Siparişi', labelEn: 'B2B Orders', paths: ['/siparisler', '/firmalar-arasi-b2b'] },
      { id: 'warehouse', labelTr: 'Depo', labelEn: 'Warehouse', paths: ['/depo'] },
      { id: 'currency', labelTr: 'Döviz', labelEn: 'Currency', paths: ['/doviz'] },
      { id: 'branchPrices', labelTr: 'Şube Fiyatları', labelEn: 'Branch Prices', paths: ['/sube-fiyatlari'] },
      { id: 'imageLibrary', labelTr: 'Resim Kütüphanesi', labelEn: 'Image Library', paths: ['/resim-kutuphanesi'] },
      { id: 'labelPrinting', labelTr: 'Etiket Yazdırma', labelEn: 'Label Printing', paths: ['/etiket-yazdirma'] },
    ],
  },
  {
    id: 'production',
    labelTr: 'Üretim',
    labelEn: 'Production',
    items: [
      { id: 'orders', labelTr: 'Üretim Fişleri', labelEn: 'Production Orders', paths: ['/uretim/fisler', '/uretim/yeni', '/uretim/fis'] },
      { id: 'planning', labelTr: 'Planlama', labelEn: 'Planning', paths: ['/uretim/planlama'] },
      { id: 'workshop', labelTr: 'Atölye', labelEn: 'Workshop', paths: ['/uretim/atolye'] },
      { id: 'tree', labelTr: 'Ürün Ağacı', labelEn: 'Product Tree', paths: ['/uretim/agac'] },
      { id: 'reports', labelTr: 'Üretim Raporları', labelEn: 'Production Reports', paths: ['/uretim/raporlar'] },
      { id: 'definitions', labelTr: 'Tanımlar', labelEn: 'Definitions', paths: ['/uretim/tanimlar'] },
      { id: 'recipes', labelTr: 'Reçeteler', labelEn: 'Recipes', paths: ['/basit-uretim-receteleri'] },
    ],
  },
  {
    id: 'sales_system',
    labelTr: 'Satış Sistemi',
    labelEn: 'Sales System',
    items: [
      { id: 'kitchenScreen', labelTr: 'Mutfak Ekranı', labelEn: 'Kitchen Screen', paths: ['/mutfak-ekrani-secimi', '/qr-menu/kitchen'] },
      { id: 'fastSale', labelTr: 'Hızlı Satış', labelEn: 'Fast Sale', paths: ['/hizli-satis'] },
      { id: 'restaurant', labelTr: 'Restoran', labelEn: 'Restaurant', paths: ['/restoran', '/restaurant-orders'] },
      { id: 'orderManagement', labelTr: 'Paket Yönetim Sistemi', labelEn: 'Order Management', paths: ['/siparis-yonetimi', '/tum-siparisler'] },
      { id: 'qrMenu', labelTr: 'QR Menü', labelEn: 'QR Menu', paths: ['/qr-menu-management', '/qr-menu/orders', '/qr-menu/customers', '/qr-menu/tables', '/qr-menu/customer-display', '/musteri-ekrani-yonetimi'] },
      { id: 'kioskMenu', labelTr: 'Kiosk Menü', labelEn: 'Kiosk Menu', paths: ['/kiosk-menu', '/kiosk-products'] },
      { id: 'eCommerce', labelTr: 'E-Ticaret', labelEn: 'E-Commerce', paths: ['/e-commerce'] },
      { id: 'courier', labelTr: 'Kurye', labelEn: 'Courier', paths: ['/kurye'] },
      { id: 'salesReports', labelTr: 'Satış Raporları', labelEn: 'Sales Reports', paths: ['/gun-sonu', '/gun-sonlari'] },
      { id: 'integrations', labelTr: 'Entegrasyonlar', labelEn: 'Integrations', paths: ['/entegrasyonlar', '/hacioglu-urun-eslestirme'] },
      { id: 'imageLibrary', labelTr: 'Resim Kütüphanesi', labelEn: 'Image Library', paths: ['/resim-kutuphanesi'] },
    ],
  },
  {
    id: 'wms',
    labelTr: 'Akıllı Depo WMS',
    labelEn: 'Smart Warehouse WMS',
    items: [
      { id: 'wmsPanel', labelTr: 'WMS Paneli', labelEn: 'WMS Dashboard', paths: ['/wms/panel'] },
      { id: 'wmsWarehouseSetup', labelTr: 'Depo Tanımlama', labelEn: 'Warehouse Setup', paths: ['/wms/depo-tanimlama'] },
      { id: 'wmsOrderPool', labelTr: 'Sipariş Havuzu', labelEn: 'Order Pool', paths: ['/wms/siparis-havuzu'] },
      { id: 'wmsPickingSetup', labelTr: 'Toplama Ayarları', labelEn: 'Picking Settings', paths: ['/wms/toplama-ayarlari'] },
      { id: 'wmsPackingSetup', labelTr: 'Paketleme Ayarları', labelEn: 'Packing Settings', paths: ['/wms/paketleme-ayarlari'] },
      { id: 'wmsReceivingSetup', labelTr: 'Mal Kabul Ayarları', labelEn: 'Receiving Settings', paths: ['/wms/mal-kabul-ayarlari'] },
      { id: 'wmsCycleCountSetup', labelTr: 'Stok Sayım Ayarları', labelEn: 'Cycle Count Settings', paths: ['/wms/stok-sayim-ayarlari'] },
      { id: 'wmsReceivingMgmt', labelTr: 'Mal Kabul Yönetimi', labelEn: 'Receiving Management', paths: ['/wms/mal-kabul-yonetimi'] },
      { id: 'wmsCycleCountMgmt', labelTr: 'Stok Sayım Yönetimi', labelEn: 'Cycle Count Management', paths: ['/wms/stok-sayim-yonetimi'] },
      { id: 'wmsEquipment', labelTr: 'Ekipman Yönetimi', labelEn: 'Equipment Management', paths: ['/wms/ekipman-yonetimi'] },
      { id: 'wmsReports', labelTr: 'WMS Raporları', labelEn: 'WMS Reports', paths: ['/wms/raporlar'] },
    ],
  },
  {
    id: 'device_management',
    labelTr: 'Cihaz Yönetimi',
    labelEn: 'Device Management',
    items: [
      { id: 'deviceManagement', labelTr: 'Cihaz Yönetimi', labelEn: 'Device Management', paths: ['/cihaz-yonetimi'] },
      { id: 'scales', labelTr: 'Tartılar', labelEn: 'Scales', paths: ['/tartilar'] },
    ],
  },
  {
    id: 'card_management',
    labelTr: 'Kart Yönetimi',
    labelEn: 'Card Management',
    items: [
      { id: 'cards', labelTr: 'Kartlar', labelEn: 'Cards', paths: ['/kart-yonetimi/kartlar', '/kart-yonetimi/oturumlar', '/kart-yonetimi/silinen-kartlar'] },
      { id: 'accessManagement', labelTr: 'Geçiş Yönetimi', labelEn: 'Access Management', paths: ['/kart-yonetimi/gecis-yonetimi'] },
    ],
  },
  {
    id: 'branches',
    labelTr: 'Şubeler',
    labelEn: 'Branches',
    items: [
      { id: 'branches', labelTr: 'Şubeler', labelEn: 'Branches', paths: ['/branches'] },
    ],
  },
  {
    id: 'other',
    labelTr: 'Diğer',
    labelEn: 'Other',
    items: [
      { id: 'reservations', labelTr: 'Rezervasyonlar', labelEn: 'Reservations', paths: ['/reservations'] },
      { id: 'events', labelTr: 'Etkinlikler', labelEn: 'Events', paths: ['/events'] },
      { id: 'mailReports', labelTr: 'Mail Raporları', labelEn: 'Mail Reports', paths: ['/accounting/mail-raporlari'] },
      // NOT: 'users' (Kullanıcılar) bilinçli olarak YOK — yalnız ana admin görür, subadmin'e devredilemez.
    ],
  },
];

/** Subadmin için her zaman serbest rotalar (modül izni gerektirmez). Raporlar sayfası kartları ayrıca izne göre süzer. */
const SUBADMIN_FREE_PATHS = ['/profile', '/raporlar'];

function pathMatches(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}

/** Bir section izinli mi? (enabled + en az bir item açık) */
export function isSectionAllowedForSubAdmin(
  perms: AdminPermissionsMap | null | undefined,
  sectionId: string
): boolean {
  const section = perms?.[sectionId];
  if (!section || section.enabled !== true) return false;
  return Object.values(section.items || {}).some(Boolean);
}

/** Bir buton (item) izinli mi? Deny-by-default. */
export function isItemAllowedForSubAdmin(
  perms: AdminPermissionsMap | null | undefined,
  sectionId: string,
  itemId: string
): boolean {
  const section = perms?.[sectionId];
  if (!section || section.enabled !== true) return false;
  return section.items?.[itemId] === true;
}

/** Rota bir modüle eşleniyor mu? Aynı path birden fazla modülde olabilir (örn. resim kütüphanesi). */
export function findAdminModulesForPath(pathname: string): Array<{ sectionId: string; itemId: string }> {
  const matches: Array<{ sectionId: string; itemId: string }> = [];
  for (const group of ADMIN_MODULE_GROUPS) {
    for (const item of group.items) {
      if (item.paths.some((p) => pathMatches(pathname, p))) {
        matches.push({ sectionId: group.id, itemId: item.id });
      }
    }
  }
  return matches;
}

/**
 * Subadmin bu rotaya girebilir mi? Deny-by-default:
 * haritada olmayan rota (örn. /kullanicilar, /superadmin) İZİNSİZDİR.
 */
export function isPathAllowedForSubAdmin(
  perms: AdminPermissionsMap | null | undefined,
  pathname: string
): boolean {
  if (SUBADMIN_FREE_PATHS.some((p) => pathMatches(pathname, p))) return true;
  const modules = findAdminModulesForPath(pathname);
  if (modules.length === 0) return false;
  return modules.some((m) => isItemAllowedForSubAdmin(perms, m.sectionId, m.itemId));
}

/** İzinli ilk modülün birincil rotası (login sonrası / izinsiz rota yönlendirmesi). */
export function getFirstAllowedPathForSubAdmin(
  perms: AdminPermissionsMap | null | undefined
): string | null {
  for (const group of ADMIN_MODULE_GROUPS) {
    for (const item of group.items) {
      if (isItemAllowedForSubAdmin(perms, group.id, item.id)) {
        return item.paths[0];
      }
    }
  }
  return null;
}

/** İzin özetini chip listesi için üretir (izinli modül etiketleri). */
export function summarizeAllowedModules(
  perms: AdminPermissionsMap | null | undefined,
  lang: 'tr' | 'en' = 'tr'
): string[] {
  const out: string[] = [];
  for (const group of ADMIN_MODULE_GROUPS) {
    for (const item of group.items) {
      if (isItemAllowedForSubAdmin(perms, group.id, item.id)) {
        out.push(lang === 'tr' ? item.labelTr : item.labelEn);
      }
    }
  }
  return Array.from(new Set(out));
}
